// Split one raw box-log event text into clean sentences, newest-first. The legacy renders an
// event as STEPS: "sentence","sentence", … — sentences double-quoted, comma-separated, written
// oldest-first. We drop the STEPS: label and the wrapping quotes and reverse, so the last
// (newest) sentence is the block's top row.
//
// A sentence is NOT quote-free: the real ones quote their own values — `booking "df4f2e27-…"`,
// `external reference "N/A"` — so neither a bare comma nor a quote pair can tell sentences apart.
// What can is the quote-comma-quote sequence (one sentence closing, the next opening), which only
// occurs BETWEEN sentences: so we peel the outer quotes and cut on that.
//
// Each sentence then comes back as SEGMENTS rather than a string, because those inner quotes are
// worth more than punctuation: they mark the machine values (ids, references, codes) the reader is
// actually hunting for, so the view renders them as inline code and drops the quotes themselves —
// the style carries the boundary the quotes used to.
//
// Fail-open: we only split a body that both opens and closes with a quote. Anything else (a plain
// line, an unterminated quote) comes back verbatim as a single row, so the log never loses
// information or mis-splits. The label goes either way — it is chrome, not content.
//
// Unavoidable ambiguity: the format has no escaping, so a sentence containing a literal
// quote-comma-quote would split in two. Preferred over the alternative (never splitting a
// quote-bearing paragraph), which collapses every real `[Box Booked]` event into one unreadable row.
export interface LogSegment {
  text: string;
  // Renders as an inline code span — a machine value the source had in quotes.
  code: boolean;
}

// One entry per row; a row with no segments is genuinely empty and the view shows «—».
export function parseLockerLogText(text: string): LogSegment[][] {
  const trimmed = text.trim();
  if (trimmed === '') return [[]];
  const body = trimmed.replace(/^STEPS:\s*/, '');
  const isQuoted = body.length >= 2 && body.startsWith('"') && body.endsWith('"');
  const sentences = isQuoted
    ? body
        .slice(1, -1)
        .split(/"\s*,\s*"/)
        .reverse()
    : [body];
  return sentences.map(toSegments);
}

// Cut one sentence into prose and the quoted values inside it, pairing quotes left to right.
//
// This runs BEFORE any decoding, and the order is load-bearing in both directions: decode first and
// an escaped `&quot;` becomes a real quote that forges a code span out of prose — and an odd number
// of them would shift every span after it. Decoding per segment (below) is what keeps the two
// stages from contaminating each other.
//
// An unpaired quote ends the scan: the remainder stays prose, quote included. A code span is never
// opened without a close, so a single stray quote can never swallow the rest of the sentence.
function toSegments(sentence: string): LogSegment[] {
  const segments: LogSegment[] = [];
  let cursor = 0;
  for (;;) {
    const open = sentence.indexOf('"', cursor);
    if (open === -1) break;
    const close = sentence.indexOf('"', open + 1);
    if (close === -1) break;
    pushSegment(segments, sentence.slice(cursor, open), false);
    pushSegment(segments, sentence.slice(open + 1, close), true);
    cursor = close + 1;
  }
  pushSegment(segments, sentence.slice(cursor), false);
  return segments;
}

// Decoding happens here — per segment, once the quote boundaries are already fixed. Empty runs are
// dropped: they are the seams between spans, and an empty `""` would otherwise paint a bare chip
// with nothing in it.
function pushSegment(segments: LogSegment[], raw: string, code: boolean): void {
  const text = decodeEntities(raw);
  if (text === '') return;
  segments.push({ text, code });
}

// The five `django.utils.html.escape()` emits, plus the two an author may hand-write.
const NAMED_ENTITIES: Record<string, string | undefined> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

// Own-property only: the name comes from log text, and a bare lookup also resolves inherited names,
// so `&constructor;` used to stringify the Object function into the row.
function namedEntity(name: string): string | undefined {
  return Object.hasOwn(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : undefined;
}

// Part of the registry arrives HTML-escaped — the legacy panel rendered the field as HTML, so the
// browser decoded it; we render inert text, so a `Transaction updated: {&#x27;state&#x27;: …}` event
// showed its entities raw. We decode them ourselves.
//
// Deliberately NOT via `innerHTML` on a throwaway element — the usual one-liner. That hands
// third-party log text to the HTML parser, which is the exact surface rendering the registry as
// plain text exists to close (a `<img onerror>` needs no `<script>` to fire). Pure string
// substitution can only ever produce a longer string, never a node.
//
// One pass over the whole input, never a chain of `.replace()` calls: `replace` does not rescan
// what it just wrote, so `&amp;#x27;` yields `&#x27;` and stops. Decoding `&amp;` in its own pass
// would feed that output back in and produce `'` — one level of escaping silently eaten.
//
// Anything unrecognized (`&bogus;`, a bare `&`, an out-of-range or surrogate code point) is left
// exactly as it came: this is a log, and a visible entity is a better failure than a mangled or
// invented character. Decoded `<`/`>` are harmless — React escapes them again on output.
function decodeEntities(text: string): string {
  return text.replace(
    /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z]+));/g,
    (match, decimal: string | undefined, hex: string | undefined, name: string | undefined) => {
      if (decimal !== undefined) return fromCodePoint(Number.parseInt(decimal, 10)) ?? match;
      if (hex !== undefined) return fromCodePoint(Number.parseInt(hex, 16)) ?? match;
      return (name === undefined ? undefined : namedEntity(name.toLowerCase())) ?? match;
    },
  );
}

// Numeric references that cannot become a character: NUL, past the Unicode range, or a lone
// surrogate (`String.fromCodePoint` would build a string no longer valid UTF-16).
function fromCodePoint(value: number): string | null {
  if (!Number.isInteger(value) || value <= 0 || value > 0x10ffff) return null;
  if (value >= 0xd800 && value <= 0xdfff) return null;
  return String.fromCodePoint(value);
}
